// src/components/PassiveSearch/ResultsTable/CandidateResultsTable.jsx

import React from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Typography, Paper, Box, Tooltip, TableSortLabel, Checkbox, Link
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; 
import HowSearchWorksDialogContent from './HowSearchWorksDialogContent';
import ScoreIndicator from '../../Common/ScoreIndicator';

const CandidateResultsTable = ({ 
    sortedCandidates, 
    sortConfig, 
    handleSortRequest, 
    headCells, 
    shortlist = [], 
    onShortlistToggle,
    onCandidateClick,
}) => {
    
    const formatPhoneNumber = (str) => {
        if (!str || str === 'None' || str === 'N/A') return 'N/A';
        return /^\+1\d{10}$/.test(str) ? str.replace(/^(\+1)(\d{3})(\d{3})(\d{4})$/, "$1 ($2) $3-$4") : str;
    }

    const tooltipContent = {
        similarity: "Measures how well the candidate's profile text matches the job description.",
        skill: "Assesses proficiency in the key skills required for the role.",
        final: "A combined score reflecting the overall match, weighting both similarity and skills."
    };

    return (
        <Paper variant="outlined" sx={{ mt: 1, width: '100%', borderRadius: '16px', borderColor: 'divider', overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    Matching Candidates
                </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: { xs: 700, sm: 800, md: 900 } }}>
                <Table stickyHeader aria-label="candidates table">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{py: 1}}></TableCell>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align={headCell.numeric ? 'right' : 'left'}
                                    sortDirection={sortConfig.key === headCell.id ? sortConfig.direction : false}
                                    sx={{py: 1}}
                                >
                                    <TableSortLabel 
                                        active={sortConfig.key === headCell.id} 
                                        direction={sortConfig.key === headCell.id ? sortConfig.direction : 'asc'} 
                                        onClick={() => handleSortRequest(headCell.id)}
                                        disabled={!headCell.sortable}
                                    >
                                        {headCell.label}
                                        {headCell.id === 'final_score' && (
                                            <Tooltip title={<HowSearchWorksDialogContent />} arrow>
                                                <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: '1rem', color: 'text.secondary', verticalAlign: 'middle' }} />
                                            </Tooltip>
                                        )}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedCandidates.map((candidate) => { 
                            const isShortlisted = shortlist.some(c => c.uuid === candidate.uuid);
                            return (
                                <TableRow hover key={candidate.uuid} selected={isShortlisted}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={isShortlisted}
                                            onChange={() => onShortlistToggle(candidate.uuid)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Link component="button" variant="body2" onClick={() => onCandidateClick(candidate)} sx={{ fontWeight: 500 }}>
                                            {candidate.name || 'N/A'}
                                        </Link>
                                        <Typography variant="caption" color="text.secondary" display="block">{candidate.email || 'N/A'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">{formatPhoneNumber(candidate.phone)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <ScoreIndicator score={candidate.similarity_score} type="linear" tooltipTitle={tooltipContent.similarity} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <ScoreIndicator score={candidate.skill_score} type="linear" tooltipTitle={tooltipContent.skill} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <ScoreIndicator score={candidate.final_score} type="radial" tooltipTitle={tooltipContent.final} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default CandidateResultsTable;